import { useState } from "react";
import AudioReactRecorder, { RecordState } from "audio-react-recorder";
import {
  IconCheck,
  IconMicrophone,
  IconPlayerStopFilled,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import { useLanguage } from "../../../../stores/LanguageStore";
import {
  ActionIcon,
  Button,
  Group,
  Modal,
  Stack,
  Text,
  Tooltip,
  useMantineTheme,
} from "@mantine/core";
import BackButton from "../../../../components/View/BackButton";

let timer;

const SmvEditor = ({ setFile, disabled, inFile, defaultOpened, opener }) => {
  const lang = useLanguage((s) => s.language);
  const [open, setOpen] = useState(defaultOpened || false);
  const [voiceFile, setVoiceFile] = useState(null);
  const [duration, setDuration] = useState(0);
  const [goingDuration, setGoingDuration] = useState(0);
  const theme = useMantineTheme();

  const [permissionStatus, setPermissionStatus] = useState(false);
  const [recordState, setRecordState] = useState(RecordState.STOP);
  const startRecord = () => {
    timer = setInterval(() => {
      setGoingDuration((prev) => prev + 100);
    }, 100);
    setVoiceFile(null);
    getPermission().then((res) => setRecordState(RecordState.START));
  };
  const stopRecord = () => {
    setRecordState(RecordState.STOP);
    setGoingDuration(0);
    setDuration(30);
    clearInterval(timer);
  };
  const onStop = (audioData) => {
    // console.log("audioData", audioData);
    setVoiceFile(audioData);
    setTimeout(() => {
      setDuration(document.getElementById("audio").duration);
    }, 100);
  };

  const getPermission = async () => {
    let mediaStream;
    try {
      const constraints = { audio: true };
      mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
    } catch (ex) {
      if (ex instanceof DOMException) {
        if (ex.name === "NotAllowedError") {
          setPermissionStatus("denied");
        } else if (ex.name === "NotFoundError") {
          setPermissionStatus("not found");
        } else {
          setPermissionStatus("denied");
        }
      } else {
        setPermissionStatus("denied");
      }
    }

    if (mediaStream) {
      setPermissionStatus("granted");
    }
  };

  const handleOpen = () => {
    setOpen(true);
    getPermission();
  };
  const handleClose = () => {
    setOpen(false);
  };

  const handleClear = () => {
    setVoiceFile(null);
    setDuration(0);
  }

  const submitAudio = () => {
    let f = new File([voiceFile.blob], "audio_enregistré.wav", {
      type: "audio/wav",
    });
    setFile(f);
    handleClose();
  };

  return (
    <>
      {
        opener ? opener({ handleOpen }) :
          <Button
            leftSection={<IconMicrophone />}
            size="md"
            onClick={handleOpen}
            disabled={disabled}
            mr={inFile ? "auto" : undefined}
            variant={inFile ? "light" : undefined}
          >
            {inFile ? "Enregistrer un audio" : lang.model_smv.smv_editor.button_label}
          </Button>
      }

      <Modal
        opened={open}
        onClose={handleClose}
        size={600}
        title={lang.model_smv.smv_editor.dialog.title}
        withCloseButton={recordState === RecordState.STOP}
        centered
        zIndex={999}
      >
        <Stack>
          {permissionStatus === "denied" && (
            <Text c="red" mb={4} size="lg">
              {lang.model_smv.smv_editor.dialog.need_permission}
            </Text>
          )}
          {permissionStatus === "not found" && (
            <Group mb={4}>
              <IconMicrophone color="error" />
              <Text c="error" ml={4}>
                {lang.model_smv.smv_editor.dialog.no_mic}
              </Text>
            </Group>
          )}
          {permissionStatus === "granted" && !inFile && (
            <Text mb={4} sx={{ opacity: 0.5 }}>
              {lang.model_smv.smv_editor.dialog.length}
            </Text>
          )}
          <Stack
            pos="relative"
            style={{ border: "1px solid #00000012" }}
            alignItems="center"
          >
            <AudioReactRecorder
              state={recordState}
              onStop={onStop}
              canvasHeight={200}
              canvasWidth={510}
              foregroundColor={theme.colors.primary[6]}
              backgroundColor={"#fff"}
            />

            <Text fz={18} pos="absolute" top={5} right={10}>
              {(Math.round(goingDuration * 100) / 100000).toFixed(2)}s
            </Text>
          </Stack>

          <Group direction="row" justify="center" gap={"xl"} mb={24}>
            <Tooltip label={lang.model_smv.smv_editor.dialog.save}>
              <ActionIcon
                onClick={startRecord}
                color="primary"
                size={"xl"}
                disabled={
                  permissionStatus === "denied" ||
                  recordState === RecordState.START
                }
              >
                <IconMicrophone size={24} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label={lang.model_smv.smv_editor.dialog.stop}>
              <ActionIcon
                onClick={stopRecord}
                color="primary"
                size={"xl"}
                disabled={
                  permissionStatus === "denied" ||
                  recordState === RecordState.STOP
                }
              >
                <IconPlayerStopFilled fontSize="large" />
              </ActionIcon>
            </Tooltip>
          </Group>

          {voiceFile && (
            <>
              <audio
                id="audio"
                controls
                src={voiceFile ? voiceFile.url : null}
                style={{ width: "100%" }}
              ></audio>
              {!inFile && (duration < 22 || duration > 50) && (
                <Text ta="center" c="red" fontSize={12} mt={1}>
                  {lang.model_smv.smv_editor.dialog.cant_use}{" "}
                  {duration < 22
                    ? lang.model_smv.smv_editor.dialog.min_length
                    : lang.model_smv.smv_editor.dialog.max_length}
                </Text>
              )}
            </>
          )}
        </Stack>
        <Group justify="space-between" mt={24}>
          <BackButton
            onClick={handleClose}
            label={lang.model_smv.smv_editor.dialog.close}
            disabled={recordState !== RecordState.STOP}
            size="md"
          />
          <Button color="red" variant="light" onClick={handleClear} leftSection={<IconTrash size={18} />} ml={"auto"} size="md" disabled={!voiceFile} >
            {lang.global.delete}
          </Button>
          <Button
            leftSection={<IconCheck />}
            onClick={submitAudio}
            disabled={
              !inFile && (duration < 22 || duration > 50) || recordState !== RecordState.STOP || !voiceFile
            }
            size="md"
          >
            {lang.model_smv.smv_editor.dialog.use}
          </Button>
        </Group>
      </Modal>
    </>
  );
};

export default SmvEditor;
